import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, X, ArrowUp, ArrowDown, Star, Image as ImageIcon, GripVertical } from "lucide-react";
import { api, imageUrl, formatApiErrorDetail } from "../../lib/api";
import { useCategories } from "../../lib/useCategories";

const EMPTY = {
  slug: "",
  title: { id: "", en: "" },
  location: "",
  year: new Date().getFullYear(),
  category: "residensial",
  client: "",
  scope: { id: "", en: "" },
  summary: { id: "", en: "" },
  description: { id: "", en: "" },
  cover_image: "",
  gallery: [],
  status: "published",
  featured: false,
  sort_order: 0,
};

export default function ProjectEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [project, setProject] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [uploading, setUploading] = useState({ count: 0, total: 0 });
  const [dragGalleryIndex, setDragGalleryIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [dropActive, setDropActive] = useState(false);
  const { categories } = useCategories();

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/projects/${id}`)
      .then((res) => {
        const d = res.data;
        const gallery = (d.gallery || []).map((g) =>
          typeof g === "string"
            ? { path: g, caption: { id: "", en: "" } }
            : { path: g.path || "", caption: g.caption || { id: "", en: "" } }
        );
        setProject({ ...EMPTY, ...d, gallery, title: d.title || d.name || { id: "", en: "" } });
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const update = (patch) => setProject((p) => ({ ...p, ...patch }));
  const setBL = (key, l, v) =>
    update({ [key]: { ...project[key], [l]: v } });

  const uploadCover = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    update({ cover_image: data.path });
  };

  const uploadGalleryFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading({ count: 0, total: arr.length });
    let newItems = [];
    for (let i = 0; i < arr.length; i++) {
      try {
        const form = new FormData();
        form.append("file", arr[i]);
        const { data } = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newItems.push({ path: data.path, caption: { id: "", en: "" } });
      } catch (err) {
        // continue uploading the rest
      }
      setUploading({ count: i + 1, total: arr.length });
    }
    if (newItems.length) {
      setProject((p) => ({ ...p, gallery: [...(p.gallery || []), ...newItems] }));
    }
    setUploading({ count: 0, total: 0 });
  };

  const removeGalleryItem = (i) =>
    update({ gallery: project.gallery.filter((_, idx) => idx !== i) });

  const moveGallery = (from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= project.gallery.length) return;
    const arr = [...project.gallery];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    update({ gallery: arr });
  };

  const setGalleryCaption = (i, lang, v) => {
    const arr = [...project.gallery];
    arr[i] = {
      ...arr[i],
      caption: { ...(arr[i].caption || { id: "", en: "" }), [lang]: v },
    };
    update({ gallery: arr });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...project, year: Number(project.year) };
      if (isNew) {
        const { data } = await api.post("/projects", payload);
        navigate(`/admin/projects/${data.id}`);
      } else {
        await api.put(`/projects/${id}`, payload);
        navigate("/admin/projects");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-[#5d5f64]">Loading…</div>;

  return (
    <div className="p-10 max-w-[980px]" data-testid="admin-project-edit">
      <Link
        to="/admin/projects"
        className="inline-flex items-center gap-2 text-sm text-[#5d5f64] hover:text-[#16181c] mb-8"
        data-testid="admin-back-to-projects"
      >
        <ArrowLeft size={14} /> Back to projects
      </Link>
      <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em] mb-10">
        {isNew ? "New project" : "Edit project"}
      </h1>

      <form onSubmit={onSubmit} className="space-y-10" data-testid="admin-project-form">
        <Section title="Title (bilingual)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title (Indonesian)">
              <input
                required
                type="text"
                className="input"
                value={project.title.id}
                onChange={(e) => setBL("title", "id", e.target.value)}
                data-testid="project-title-id"
              />
            </Field>
            <Field label="Title (English)">
              <input
                required
                type="text"
                className="input"
                value={project.title.en}
                onChange={(e) => setBL("title", "en", e.target.value)}
                data-testid="project-title-en"
              />
            </Field>
          </div>
          <Field label="Slug (auto-generated if blank; must be unique)">
            <input
              type="text"
              className="input font-mono"
              value={project.slug}
              onChange={(e) => update({ slug: e.target.value })}
              placeholder="e.g. rumah-tinggal-bukit-serpong"
              data-testid="project-slug"
            />
          </Field>
        </Section>

        <Section title="Meta">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Location">
              <input
                required
                type="text"
                className="input"
                value={project.location}
                onChange={(e) => update({ location: e.target.value })}
                data-testid="project-location"
              />
            </Field>
            <Field label="Year">
              <input
                required
                type="number"
                className="input"
                value={project.year}
                onChange={(e) => update({ year: e.target.value })}
                data-testid="project-year"
              />
            </Field>
            <Field label="Category">
              <select
                className="input"
                value={project.category}
                onChange={(e) => update({ category: e.target.value })}
                data-testid="project-category"
              >
                {categories.length === 0 && (
                  <option value="residensial">Residensial</option>
                )}
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name?.id || c.slug}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Client (optional)">
              <input
                type="text"
                className="input"
                value={project.client}
                onChange={(e) => update({ client: e.target.value })}
                data-testid="project-client"
              />
            </Field>
          </div>
        </Section>

        <Section title="Scope / Lingkup">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Scope (Indonesian)">
              <input
                type="text"
                className="input"
                value={project.scope.id}
                onChange={(e) => setBL("scope", "id", e.target.value)}
                data-testid="project-scope-id"
              />
            </Field>
            <Field label="Scope (English)">
              <input
                type="text"
                className="input"
                value={project.scope.en}
                onChange={(e) => setBL("scope", "en", e.target.value)}
                data-testid="project-scope-en"
              />
            </Field>
          </div>
        </Section>

        <Section title="Summary (short — for cards)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Summary (Indonesian)">
              <textarea
                rows={2}
                className="input"
                value={project.summary.id}
                onChange={(e) => setBL("summary", "id", e.target.value)}
                data-testid="project-summary-id"
              />
            </Field>
            <Field label="Summary (English)">
              <textarea
                rows={2}
                className="input"
                value={project.summary.en}
                onChange={(e) => setBL("summary", "en", e.target.value)}
                data-testid="project-summary-en"
              />
            </Field>
          </div>
        </Section>

        <Section title="Description (long — for detail page)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Description (Indonesian)">
              <textarea
                rows={8}
                className="input"
                value={project.description.id}
                onChange={(e) => setBL("description", "id", e.target.value)}
                data-testid="project-desc-id"
              />
            </Field>
            <Field label="Description (English)">
              <textarea
                rows={8}
                className="input"
                value={project.description.en}
                onChange={(e) => setBL("description", "en", e.target.value)}
                data-testid="project-desc-en"
              />
            </Field>
          </div>
          <p className="text-xs text-[#5d5f64] mt-3">
            Use blank lines to separate paragraphs.
          </p>
        </Section>

        <Section title="Cover image">
          <ImageUploader
            value={project.cover_image}
            onChange={(v) => update({ cover_image: v })}
            onFile={uploadCover}
            testid="project-cover"
          />
        </Section>

        <Section title="Gallery (drag to reorder, click 'Set as cover' to feature)">
          <div
            className={`border-2 border-dashed ${
              dropActive ? "border-[#16181c] bg-[#f3f3f0]" : "border-[#e3e3e1] bg-white"
            } p-8 text-center mb-6 transition-colors`}
            onDragOver={(e) => {
              e.preventDefault();
              setDropActive(true);
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDropActive(false);
              if (e.dataTransfer?.files) uploadGalleryFiles(e.dataTransfer.files);
            }}
            data-testid="project-gallery-dropzone"
          >
            <ImageIcon size={28} className="mx-auto text-[#5d5f64] mb-3" />
            <div className="text-sm text-[#16181c] mb-2">
              Drop images here from your computer
            </div>
            <div className="text-xs text-[#5d5f64] mb-4">
              or click below to choose files · JPG / PNG / WEBP
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 border border-[#16181c] bg-white px-4 py-2 text-sm hover:bg-[#f3f3f0]"
              data-testid="project-gallery-pick"
            >
              <Upload size={14} /> Choose files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) uploadGalleryFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="project-gallery-input"
            />
            {uploading.total > 0 && (
              <div className="mt-4 text-xs text-[#5d5f64]" data-testid="project-gallery-progress">
                Uploading {uploading.count} / {uploading.total}…
              </div>
            )}
          </div>

          <div className="space-y-3">
            {(project.gallery || []).map((g, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => setDragGalleryIndex(i)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragGalleryIndex === null || dragGalleryIndex === i) return;
                  const arr = [...project.gallery];
                  const [moved] = arr.splice(dragGalleryIndex, 1);
                  arr.splice(i, 0, moved);
                  setProject({ ...project, gallery: arr });
                  setDragGalleryIndex(i);
                }}
                onDragEnd={() => setDragGalleryIndex(null)}
                className={`border border-[#e3e3e1] p-4 flex gap-4 items-start ${
                  dragGalleryIndex === i ? "bg-[#f3f3f0]" : ""
                }`}
                data-testid={`project-gallery-row-${i}`}
              >
                <div className="cursor-grab active:cursor-grabbing text-[#5d5f64] pt-2" aria-label="Drag">
                  <GripVertical size={16} />
                </div>
                <div className="relative w-32 aspect-[4/3] bg-[#1b1c1e] overflow-hidden flex-shrink-0 rounded-lg">
                  <img src={imageUrl(g.path)} alt="" className="w-full h-full object-cover" />
                  {project.cover_image === g.path && (
                    <div className="absolute top-1 left-1 bg-white text-[#16181c] text-[10px] uppercase tracking-wider px-2 py-0.5 inline-flex items-center gap-1">
                      <Star size={10} /> Cover
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="input"
                      placeholder="Caption (Indonesian)"
                      value={g.caption?.id || ""}
                      onChange={(e) => setGalleryCaption(i, "id", e.target.value)}
                      data-testid={`project-gallery-caption-id-${i}`}
                    />
                    <input
                      type="text"
                      className="input"
                      placeholder="Caption (English)"
                      value={g.caption?.en || ""}
                      onChange={(e) => setGalleryCaption(i, "en", e.target.value)}
                      data-testid={`project-gallery-caption-en-${i}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => update({ cover_image: g.path })}
                      className={`inline-flex items-center gap-1 px-2 py-1 border ${
                        project.cover_image === g.path
                          ? "border-[#16181c] bg-[#16181c] text-white"
                          : "border-[#e3e3e1] bg-white text-[#16181c] hover:bg-[#f3f3f0]"
                      }`}
                      data-testid={`project-gallery-set-cover-${i}`}
                    >
                      <Star size={10} /> {project.cover_image === g.path ? "Cover" : "Set as cover"}
                    </button>
                    <span className="text-[#5d5f64]">Order #{i + 1}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => moveGallery(i, -1)}
                    disabled={i === 0}
                    className="p-1 text-[#16181c] disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveGallery(i, 1)}
                    disabled={i === project.gallery.length - 1}
                    className="p-1 text-[#16181c] disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeGalleryItem(i)}
                    className="p-1 text-[#16181c]"
                    aria-label="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Status">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Status">
              <select
                className="input"
                value={project.status}
                onChange={(e) => update({ status: e.target.value })}
                data-testid="project-status"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                className="input"
                value={project.sort_order}
                onChange={(e) => update({ sort_order: Number(e.target.value) })}
                data-testid="project-sort-order"
              />
            </Field>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-3 text-sm pb-2">
                <input
                  type="checkbox"
                  checked={!!project.featured}
                  onChange={(e) => update({ featured: e.target.checked })}
                  data-testid="project-featured"
                />
                <Star size={14} /> Featured
              </label>
            </div>
          </div>
        </Section>

        {error && (
          <div className="text-sm text-[#16181c] bg-[#f3f3f0] border border-[#e3e3e1] px-4 py-3">
            {error}
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
            data-testid="project-save"
          >
            {saving ? "Saving…" : isNew ? "Create project" : "Save changes"}
          </button>
          <Link to="/admin/projects" className="text-sm text-[#5d5f64] hover:text-[#16181c]">
            Cancel
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #e3e3e1;
          padding: 10px 12px;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          color: #16181c;
        }
        .input:focus { outline: none; border-color: #16181c; }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-[#e3e3e1] p-6">
      <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-5">{title}</div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#5d5f64] mb-2">{label}</span>
      {children}
    </label>
  );
}

export function ImageUploader({ value, onChange, onFile, testid }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onFile(file);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-start gap-6">
      <div className="w-44 aspect-[4/3] bg-[#1b1c1e] overflow-hidden border border-[#e3e3e1]">
        {value ? (
          <img src={imageUrl(value)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>
      <div className="flex-1">
        <label className="inline-flex items-center gap-2 border border-[#e3e3e1] bg-white px-4 py-2 cursor-pointer hover:bg-[#f3f3f0] text-sm">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handle}
            data-testid={`${testid}-input`}
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="ml-4 text-sm text-[#5d5f64] hover:text-[#16181c]"
            data-testid={`${testid}-remove`}
          >
            Remove
          </button>
        )}
        <p className="mt-3 text-xs text-[#5d5f64]">
          JPG, PNG, or WEBP up to a few MB. Wide aspect ratios recommended for covers.
        </p>
      </div>
    </div>
  );
}
